// Opens the Import files dialog from any page: a fresh app per open, unmounted on close, so
// every import starts empty. Each entry point passes its start screen; the result goes to the
// page through `onImported` and the window events the list pages already listen for.

import { createApp, h } from 'vue';
import { i18n } from '../../i18n';
import ImportFilesDialog from './ImportFilesDialog.vue';
import type { ImportIntent } from '../../utils/import/importTypes';
import type { ImportDestination } from '../../utils/import/importScreenState';
import type { ImportEntryName } from '../../utils/import/importTelemetry';
import { destinationName, resultText, type Translate } from '../../utils/import/importTexts';
import type { ImportOutcome } from './runImport';

export interface OpenImportFilesOptions {
  intent: ImportIntent;
  entry: ImportEntryName;
  /** Called after a save that created something, before the result toast. */
  onImported?: (outcome: ImportOutcome) => void;
}

/** The start screen of each entry point, and its telemetry `entry`. */
export const IMPORT_ENTRY_POINTS: Record<string, { intent: ImportIntent; entry: ImportEntryName }> = {
  recordings: { intent: 'recording', entry: 'recordings' },
  planned: { intent: 'planned', entry: 'planned' },
  waypoints: { intent: 'waypoints', entry: 'waypoints' },
  // The profile header's button: no start screen, each file sorted by what it holds.
  profile: { intent: 'none', entry: 'profile' },
  none: { intent: 'none', entry: 'none' }
};

let openApp: { unmount: () => void } | null = null;

export function openImportFiles(options: OpenImportFilesOptions): void {
  if (openApp) return;
  const host = document.createElement('div');
  host.className = 'import-files-host';
  document.body.appendChild(host);
  const previousOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  const app = createApp({
    render: () =>
      h(ImportFilesDialog, {
        intent: options.intent,
        entry: options.entry,
        onClose: (outcome: ImportOutcome | null) => {
          app.unmount();
          host.remove();
          document.body.style.overflow = previousOverflow;
          openApp = null;
          if (outcome) finish(outcome, options);
        }
      })
  });
  app.use(i18n);
  app.mount(host);
  openApp = app;
}

function finish(outcome: ImportOutcome, options: OpenImportFilesOptions): void {
  const recordings = outcome.created.filter(c => c.destination === 'recordings');
  if (recordings.length) {
    // The recordings list reloads itself; My Peaks checks the new recordings for peaks.
    window.reloadRecordedTrails?.();
    window.dispatchEvent(
      new CustomEvent('hiiker:recordings-imported', {
        detail: { recordings: recordings.map(r => ({ id: r.id, title: r.title, shareableUrl: r.url ?? null })) }
      })
    );
  }
  if (outcome.created.length) options.onImported?.(outcome);
  showResultToast(outcome, i18n.global.t as Translate);
}

const VIEW_URLS: Record<ImportDestination, string | null> = {
  recordings: '/user/recordings',
  plannedHikes: '/user/hikes',
  waypoints: null
};

const escapeHtml = (s: string): string =>
  s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);

/** "Imported 3 recordings and 1 planned hike, 2 not imported", a View link per destination, and any failures. */
export function showResultToast(outcome: ImportOutcome, t: Translate): void {
  const destinations = Array.from(new Set(outcome.created.map(c => c.destination)));
  const linked = destinations
    .filter(d => VIEW_URLS[d] && window.location.pathname !== VIEW_URLS[d])
    .map(d => `<a href="${VIEW_URLS[d]}" class="text-yellow fw-bold">${escapeHtml(destinationName(t, d))}</a>`);
  const links = linked.length ? `<div class="mt-1">${escapeHtml(t('import_files.view'))}: ${linked.join(' · ')}</div>` : '';
  const failed = outcome.failures.length
    ? `<div class="mt-1 text-danger">${escapeHtml(t('import_files.result_failed', outcome.failures.length))}: ${escapeHtml(outcome.failures.map(f => f.name).join(', '))}</div>`
    : '';
  const headline = outcome.created.length ? resultText(t, outcome.counts, outcome.notImported) : '';
  const el = document.createElement('div');
  el.className = 'toast-container position-fixed bottom-0 end-0 p-3';
  el.style.zIndex = '1095';
  el.innerHTML = `
    <div class="toast show" role="status" aria-live="polite">
      <div class="toast-body bg-dark text-white d-flex align-items-start gap-2">
        <div class="flex-grow-1">${escapeHtml(headline)}${links}${failed}</div>
        <button type="button" class="btn-close btn-close-white" aria-label="${escapeHtml(t('import_files.cancel'))}"></button>
      </div>
    </div>`;
  document.body.appendChild(el);
  el.querySelector('.btn-close')?.addEventListener('click', () => el.remove());
  setTimeout(() => el.remove(), 15000);
}
